import { useEffect, useState } from "react";
import Blocks from "../blocks";
import { useHistory, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import { Dialog } from "@headlessui/react";
import SignupBg from "../../assets/images/study-signup.svg";
import { Field, Formik } from "formik";
import UTILS from "../../utils";
import Fields from "../formFields";
import Partials from "../partials";
import { useKeycloak } from "@react-keycloak/web";
import moment from "moment";
import { IoMdArrowBack } from "react-icons/io";
import { toast } from "react-toastify";
import { Country, State, City } from 'country-state-city';
import { updateTeacherProfile } from "../../api/user"; 

const PageEditTeacherProfile = () => {
  const history = useHistory();
  const { sid } = useParams();
  const { keycloak } = useKeycloak();

  const TeacherSid = keycloak?.idTokenParsed?.sid;

  useEffect(() => {
    if (TeacherSid !== sid) {
      history.replace("/create-virtual-class");
    }
  });

  const [isDialogOpen, _setIsDialogOpen] = useState(false);
  const [initialValues, _setInitialValues] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    mobile_number: "",
    photo: "",
    dob: "",
    countryOptions: [],
    stateOptions: [],
    cityOptions: [],
    countryCode: "",
    countryDial: "",
    user_image: "",
  });

  const setCountry = (country) => {
    const countryObj = country
      ? { value: country, label: country }
      : {
          value: "",
          label: "",
        };
    return countryObj;
  };

  const setState = (state) => {
    const stateObj = state
      ? { value: state, label: state }
      : {
          value: "",
          label: "",
        };
    return stateObj;
  };

  const setCity = (city) => {
    const cityObj = city
      ? { value: city, label: city }
      : {
          value: "",
          label: "",
        };
    return cityObj;
  };

  const setGender = (gender) => {
    const genderObj = gender
      ? { value: gender, label: gender }
      : {
          value: "",
          label: "",
        };
    return genderObj;
  };
  
  const setDateOfBirth = (dob) => {
    const DOB = dob
      ? moment(dob).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    return DOB;
  };

const getAllCountries = () => {
  const allowedCountries = [
    "India",
    "United States",
    "Canada",
    "Australia",
    "United Kingdom",
    "Germany",
    "Japan",
    "Switzerland",
    "Sweden",
    "Mexico",
  ];

  return Country.getAllCountries()
    .filter(country => allowedCountries.includes(country.name))
    .map(country => ({
      value: country.name,
      label: country.name,
      isoCode: country.isoCode
    }));
};

  const getStatesByCountry = (countryName) => {
    const country = Country.getAllCountries().find(c => c.name === countryName);
    if (!country) return [];
    return State.getStatesOfCountry(country.isoCode).map(state => ({
      value: state.name,
      label: state.name,
      isoCode: state.isoCode
    }));
  };

  const getCitiesByState = (countryName, stateName) => {
    const country = Country.getAllCountries().find(c => c.name === countryName);
    if (!country) return [];
    
    const state = State.getStatesOfCountry(country.isoCode).find(s => s.name === stateName);
    if (!state) return [];
    
    return City.getCitiesOfState(country.isoCode, state.isoCode).map(city => ({
      value: city.name,
      label: city.name
    }));
  };

  useEffect(() => {
    if (TeacherSid === sid) {
      const teacherData = keycloak?.idTokenParsed;
      
      const countryCode = Country.getAllCountries().find(
        item => item.name === teacherData?.country
      )?.isoCode;

      const newInitValues = {
        id: teacherData.sub,
        email: teacherData?.email,
        first_name: teacherData?.given_name,
        last_name: teacherData?.family_name,
        mobile_number: teacherData?.phone_number,
        photo: teacherData?.photo,
        country: setCountry(teacherData?.country),
        state: setState(teacherData?.state),
        city: setCity(teacherData?.city),
        dob: setDateOfBirth(Number(teacherData?.dob)),
        gender: setGender(teacherData?.gender),
        countryCode: countryCode || "IN",
        stateOptions: teacherData?.country ? getStatesByCountry(teacherData.country) : [],
        cityOptions: teacherData?.country && teacherData?.state ? 
          getCitiesByState(teacherData.country, teacherData.state) : [],
        countryOptions: getAllCountries()
      };

      _setInitialValues({ ...initialValues, ...newInitValues });
    }
  }, []);

  const { isLoading, error, isError, mutate, data } = useMutation(
    updateTeacherProfile,
    {
      onSuccess: (data) => {
        keycloak
          .updateToken(720000)
          .then(function (refreshed) {
            if (refreshed) {
              toast.success("Profile updated successfully!");
               console.log("Token updated. Redirecting to home...");
        setTimeout(() => {
          window.location.href = "/teacher/home"; // Force reload after toast
        }, 200); 
               } else {
              console.log("Token NOT updated after update profile");
            }
          })
          .catch(function () {
            console.log("Error in Token updated after update profile");
          });
        history.push("/teacher/home");
      },
      onError: (err) => {
        toast.error(err.message);
        console.log(err);
      },
    }
  );

  const updateProfile = (values, action) => {
    const teacherData = keycloak?.idTokenParsed;

    const {
      first_name,
      last_name,
      photo,
      email,
      country,
      state,
      city,
      dob,
      gender,
      countryCode,
      countryDial,
      mobile_number,
    } = values;
    const updatedPhoto = photo.replace("data:", "").replace(/^.+,/, "");
    const countryValue = country?.value;
    const stateValue = state?.value;
    const cityValue = city?.value;

    let mobileNo = "";
    if (countryDial && mobile_number) {
      mobileNo = `+${countryDial}-${mobile_number?.slice(
        values?.countryDial?.length
      )}`;
    }

    const dobValue = UTILS.time.getDateTimeStamp(dob);

    let teacher_obj = {
      id: values?.id,
      data: {
        id: values?.id,
        first_name,
        last_name,
        mobile_number: mobileNo ? mobileNo : values?.mobile_number,
        dob: dobValue,
        country: countryValue,
        state: stateValue,
        city: cityValue,
        gender: gender?.value,
      },
    };

    if (teacherData?.photo !== updatedPhoto) {
      const newTeacher_obj = {
        ...teacher_obj,
        data: {
          ...teacher_obj?.data,
          photo: updatedPhoto,
        },
      };
      mutate(newTeacher_obj);
    } else {
      mutate(teacher_obj);
    }
  };

  const handleCountryChangeFunc = (e, values) => {
    const states = getStatesByCountry(e.value);
    const reInitialize = {
      country: { value: e?.value, label: e?.label },
      state: { label: "", value: "" },
      city: { label: "", value: "" },
      stateOptions: states,
      cityOptions: [],
      mobile_number: values?.mobile_number,
      countryCode: e.isoCode || values?.countryCode,
      countryDial: values?.countryDial,
      dob: values?.dob,
      gender: values.gender,
    };
    _setInitialValues({ ...initialValues, ...reInitialize });
  };

  const handleStateChangeFunc = (e, values) => {
    const cities = getCitiesByState(values.country.value, e.value);
    const reInitialize = {
      state: { value: e?.value, label: e?.label },
      city: { value: "", label: "" },
      cityOptions: cities,
    };
    _setInitialValues({ ...initialValues, ...reInitialize });
  };
  
  const goToHome = () => {
    history.push("/");
  };
  
  return (
    <div className={"page PageStudentCompleteProfile"}>
      <div className="container mx-auto">
        <div className="mt-16 flex justify-center items-center md:hidden gap-2">
          <IoMdArrowBack
            size={20}
            className="cursor-pointer"
            onClick={() => goToHome()}
          />
          <h3 className={"text-xl text-center font-['Poppins-Bold']"}>
            {"Edit your teacher's profile"}
          </h3>
        </div>

        <div className="relative flex flex-col md:flex-row items-center">
          <Dialog
            open={isDialogOpen}
            onClose={() => _setIsDialogOpen(false)}
            className="absolute w-80 h-96 z-10 right-1/2 bottom-1/2 translate-x-2/4 translate-y-2/4 border-2 border-b-blue-800"
          >
            <Dialog.Overlay />
            <Dialog.Title>Camera</Dialog.Title>
            <Dialog.Description></Dialog.Description>
            <button onClick={() => _setIsDialogOpen(false)}>Capture</button>
          </Dialog>
          <div className="bgWrapper md:flex justify-center items-center bg-white">
            <img src={SignupBg} alt="" draggable={false} />
          </div>
          <div className="pageContent w-full max-w-lg mx-auto md:mr-0 md:ml-auto mt-4 md:mt-0  mb-4 p-4 rounded-xl">
            <div className="hidden flex  md:flex">
              <IoMdArrowBack
                size={40}
                className="mr-3 cursor-pointer"
                onClick={() => goToHome()}
              />
              <h4 className={"text-2xl font-subheading"}>
                {"Edit your teacher's profile"}
              </h4>
            </div>

            <Blocks.ErrorBoundary>
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={UTILS.forms.editTeachersProfile}
                onSubmit={updateProfile}
              >
                {({ setFieldValue, handleSubmit, isSubmitting, values }) => (
                  <form
                    onSubmit={handleSubmit}
                    className="pt-6 pb-8"
                    noValidate
                  >
                    <div className="md:grid md:grid-cols-2 gap-4">
                      <Field
                        type={"text"}
                        name="first_name"
                        placeholder="First Name"
                        iconBefore={
                          <i className={`text-xl icon icon-user`}></i>
                        }
                        component={Fields.FieldInput}
                      />
                      <Field
                        type="text"
                        name="last_name"
                        placeholder="Last Name"
                        iconBefore={
                          <i className={`text-xl icon icon-user`}></i>
                        }
                        component={Fields.FieldInput}
                      />
                    </div>
                    <Field
                      type="email"
                      name="email"
                      placeholder="Email Id"
                      iconBefore={<i className={`text-xl icon icon-mail1`}></i>}
                      component={Fields.FieldInput}
                      disabled={true}
                    />
                    <Field
                      type="text"
                      name="mobile_number"
                      placeholder="Mobile number"
                      iconBefore={<i className={`text-xl icon icon-phone`}></i>}
                      component={Fields.FieldInputPhoneNumber}
                    />
                    <div className="md:grid md:grid-cols-2 gap-4">
                      <Field
                        type="date"
                        name="dob"
                        placeholder="Date of Birth"
                        iconBefore={<span className="italic font-bold">DOB</span>}Add commentMore actions
                        max={moment().format("YYYY-MM-DD")}
                        component={Fields.FieldInput}
                      />
                       <Field 
                        name="gender" 
                        component={Fields.FieldSelect}
                        options={[
                          { value: "male", label: "Male" }, 
                          { value: "female", label: "Female" },
                          { value: "other", label: "Other" }
                        ]}
                        iconBefore={<span className="italic font-bold text-gray-400">Gender</span>}
                        placeholder="Select Gender"
                      />
                    </div>
                    <Field
                      type="text"
                      name="country"
                      placeholder="Country"
                      iconBefore={
                        <span className="italic font-bold">Country</span>
                      }
                      options={initialValues.countryOptions}
                      component={Fields.FieldSelect}
                      onChange={(e) => {
                        setFieldValue("stateOptions", getStatesByCountry(e.value));
                        handleCountryChangeFunc(e, values);
                      }}
                    />
                    <Field
                      type="text"
                      name="state"
                      placeholder="state"
                      options={values?.stateOptions}
                      iconBefore={
                        <span className="italic font-bold">State</span>
                      }
                      component={Fields.FieldSelect}
                      onChange={(e) => {
                        const cities = getCitiesByState(values.country.value, e.value);
                        setFieldValue("cityOptions", cities);
                        handleStateChangeFunc(e, values);
                      }}
                    />
                    <Field
                      type="text"
                      name="city"
                      placeholder="City"
                      iconBefore={
                        <span className="italic font-bold">City</span>
                      }
                      options={values?.cityOptions}
                      component={Fields.FieldSelectCity}
                    />

                    <Field
                      name={"photo"}
                      component={Fields.FieldCameraInput}
                      isFileUpload
                    />
                    <button
                      onClick={() => handleSubmit()}
                      disabled={isLoading}
                      className="bg-black w-full hover:bg-zinc-700 text-white inline-flex justify-center font-bold py-3 px-4 rounded focus:outline-none focus:shadow-outline transition ease-in-out duration-150"
                      type="submit"
                    >
                      {isLoading && <Partials.PartialLoading />}
                      Save changes
                    </button>
                  </form>
                )}
              </Formik>
            </Blocks.ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEditTeacherProfile;